import { z } from 'zod'

// =====================
// Form Enums
// =====================

export const FormStatusEnum = z.enum([
  'DRAFT',
  'PUBLISHED',
  'CLOSED',
  'ARCHIVED',
])
export type FormStatus = z.infer<typeof FormStatusEnum>

export const FormAccessTypeEnum = z.enum([
  'PUBLIC', // Anyone with the link
  'EMAIL_RESTRICTED', // Only specified emails
  'PASSWORD', // Password protected
  'AUTHENTICATED', // Requires Scrambl login
])
export type FormAccessType = z.infer<typeof FormAccessTypeEnum>

export const FormFieldTypeEnum = z.enum([
  // Text types
  'SHORT_TEXT',
  'LONG_TEXT',
  'RICH_TEXT',
  'EMAIL',
  'PHONE',
  'URL',

  // Number types
  'NUMBER',
  'PERCENTAGE',
  'CURRENCY',

  // Date/Time types
  'DATE',
  'DATETIME',
  'TIME',

  // Selection types
  'DROPDOWN',
  'MULTISELECT',
  'CHECKBOX',
  'RADIO',
  'BOOLEAN',
  'RATING',

  // File types
  'FILE_UPLOAD',
  'MULTI_FILE_UPLOAD',

  // Reference types
  'REFERENCE',
  'MULTI_REFERENCE',

  // Layout types (non-data)
  'SECTION_HEADER',
  'DESCRIPTION_BLOCK',
  'IMAGE',
  'DIVIDER',
  'PAGE_BREAK',
])
export type FormFieldType = z.infer<typeof FormFieldTypeEnum>

export const FormTargetTypeEnum = z.enum(['MODULE', 'COLLECTION'])
export type FormTargetType = z.infer<typeof FormTargetTypeEnum>

export const FormSubmissionStatusEnum = z.enum([
  'pending',
  'processed',
  'failed',
])
export type FormSubmissionStatus = z.infer<typeof FormSubmissionStatusEnum>

// =====================
// Conditional Logic Types
// =====================

export const ConditionOperatorEnum = z.enum([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'less_than',
  'is_empty',
  'is_not_empty',
  'is_checked',
  'is_not_checked',
])
export type ConditionOperator = z.infer<typeof ConditionOperatorEnum>

export const ConditionActionEnum = z.enum(['show', 'hide', 'require'])
export type ConditionAction = z.infer<typeof ConditionActionEnum>

export const ConditionLogicEnum = z.enum(['all', 'any'])
export type ConditionLogic = z.infer<typeof ConditionLogicEnum>

export const FormConditionSchema = z.object({
  fieldId: z.string().uuid(),
  operator: ConditionOperatorEnum,
  value: z.unknown(),
})
export type FormCondition = z.infer<typeof FormConditionSchema>

export const ConditionalRuleSchema = z.object({
  action: ConditionActionEnum,
  logic: ConditionLogicEnum,
  conditions: z.array(FormConditionSchema),
})
export type ConditionalRule = z.infer<typeof ConditionalRuleSchema>

// =====================
// Form Settings Types
// =====================

export const FormImageSchema = z.object({
  url: z.string(),
  placement: z.enum([
    'above-title',
    'below-title',
    'above-button',
    'below-button',
  ]),
  type: z.enum(['logo', 'header', 'footer']),
})
export type FormImage = z.infer<typeof FormImageSchema>

export const FormBrandingSettingsSchema = z.object({
  // Original fields
  logo: z.string().optional(),
  primaryColor: z.string().optional(),
  backgroundColor: z.string().optional(),
  fontFamily: z.string().optional(),

  // New color controls
  cardBackgroundColor: z.string().optional(), // Section/card background
  headerTextColor: z.string().optional(), // Form title text
  bodyTextColor: z.string().optional(), // Labels and description text
  inputBackgroundColor: z.string().optional(), // Text input background
  inputBorderColor: z.string().optional(), // Text input border
  inputPlaceholderColor: z.string().optional(), // Placeholder text in inputs
  buttonBackgroundColor: z.string().optional(), // Submit button background
  buttonTextColor: z.string().optional(), // Submit button text
  pageBackgroundColor: z.string().optional(), // Outer page background

  // Image uploads
  images: z.array(FormImageSchema).optional(),
})
export type FormBrandingSettings = z.infer<typeof FormBrandingSettingsSchema>

export const FormThankYouSettingsSchema = z.object({
  message: z.string().optional(),
  redirectUrl: z.string().url().optional(),
  showSubmitAnother: z.boolean().optional(),
  autoResetDelaySeconds: z.number().int().min(0).max(300).optional(),
})
export type FormThankYouSettings = z.infer<typeof FormThankYouSettingsSchema>

export const FormDisplaySettingsSchema = z.object({
  hideHeader: z.boolean().optional(), // Hide the entire header section
  hideTitle: z.boolean().optional(), // Hide the form title
  hideDescription: z.boolean().optional(), // Hide the form description
  hidePoweredBy: z.boolean().optional(), // Hide "Powered by Scrambl" footer
})
export type FormDisplaySettings = z.infer<typeof FormDisplaySettingsSchema>

// Confirmation email sent to the submitter (requires an email on the
// submission — derived from an EMAIL field or an email-mapped field).
export const FormConfirmationSettingsSchema = z.object({
  sendEmail: z.boolean().optional(),
  message: z.string().optional(),
})
export type FormConfirmationSettings = z.infer<
  typeof FormConfirmationSettingsSchema
>

export const FormSettingsSchema = z.object({
  branding: FormBrandingSettingsSchema.optional(),
  thankYou: FormThankYouSettingsSchema.optional(),
  display: FormDisplaySettingsSchema.optional(),
  confirmation: FormConfirmationSettingsSchema.optional(),
  showProgressBar: z.boolean().optional(),
  showFieldNumbers: z.boolean().optional(),
})
export type FormSettings = z.infer<typeof FormSettingsSchema>

// =====================
// Field Validation Types
// =====================

export const FieldValidationSchema = z.object({
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
  patternMessage: z.string().optional(),
})
export type FieldValidation = z.infer<typeof FieldValidationSchema>

export function normalizeEmailInput(value: string): string {
  return value.trim()
}

export function isValidEmailInput(value: string): boolean {
  const normalized = normalizeEmailInput(value)
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(normalized)
}

export function normalizePhoneInput(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

export function isValidPhoneInput(value: string): boolean {
  const normalized = normalizePhoneInput(value)
  const extensionMatch = normalized.match(
    /\s+(?:ext\.?|extension|x)\s*\d{1,6}$/i,
  )
  const mainNumber = extensionMatch
    ? normalized.slice(0, extensionMatch.index).trim()
    : normalized

  if (!mainNumber || !/^\+?[0-9()\-\s./]+$/.test(mainNumber)) {
    return false
  }

  const digits = mainNumber.replace(/\D/g, '')
  if (digits.length < 7 || digits.length > 15) {
    return false
  }

  return /\d\)?$/.test(mainNumber)
}

export function isValidUrlInput(value: string): boolean {
  const normalized = value.trim()

  if (!/^https?:\/\//i.test(normalized)) {
    return false
  }

  try {
    const url = new URL(normalized)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function isValidDateInput(value: unknown): boolean {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime())
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return false
  }

  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime())
}

export function isValidDateTimeInput(value: unknown): boolean {
  return isValidDateInput(value)
}

export function isValidTimeInput(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false
  }

  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value.trim())
}

// Destination document folder for submitted files / links
export const DocumentDestinationSchema = z.object({
  enabled: z.boolean().default(false),
  // 'fixed': a pre-selected org-wide folder; 'target_record': a subfolder
  // auto-created under the record this submission creates
  mode: z.enum(['fixed', 'target_record']),
  fixedCategoryId: z.string().uuid().optional(), // mode === 'fixed'
  subfolderName: z.string().optional(), // mode === 'target_record'
})
export type DocumentDestination = z.infer<typeof DocumentDestinationSchema>

export const FileSettingsSchema = z.object({
  maxSize: z.number().optional(), // in bytes
  maxFiles: z.number().optional(),
  allowedTypes: z.array(z.string()).optional(), // MIME types
  documentDestination: DocumentDestinationSchema.optional(),
})
export type FileSettings = z.infer<typeof FileSettingsSchema>

export const FieldOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
})
export type FieldOption = z.infer<typeof FieldOptionSchema>

// =====================
// Form Field Schema
// =====================

export const FormFieldSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  sectionId: z.string().uuid().nullable().optional(),
  fieldType: FormFieldTypeEnum,
  label: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  placeholder: z.string().nullable().optional(),
  helpText: z.string().nullable().optional(),
  footerText: z.string().nullable().optional(),
  mappedField: z.string().nullable().optional(),
  isRequired: z.boolean().default(false),
  validation: FieldValidationSchema.nullable().optional(),
  options: z.array(FieldOptionSchema).nullable().optional(),
  defaultValue: z.unknown().nullable().optional(),
  order: z.number().default(0),
  width: z.enum(['full', 'half', 'third']).default('full'),
  conditions: z.array(ConditionalRuleSchema).nullable().optional(),
  fileSettings: FileSettingsSchema.nullable().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type FormField = z.infer<typeof FormFieldSchema>

// =====================
// Form Section Schema
// =====================

export const FormSectionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable().optional(),
  footerText: z.string().nullable().optional(),
  order: z.number().default(0),
  conditions: z.array(ConditionalRuleSchema).nullable().optional(),
  fields: z.array(FormFieldSchema).optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
})
export type FormSection = z.infer<typeof FormSectionSchema>

// =====================
// Form Schema
// =====================

export const FormSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  creatorId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable().optional(),
  status: FormStatusEnum,
  targetType: FormTargetTypeEnum,
  targetModuleType: z.string().nullable().optional(),
  targetCollectionId: z.string().uuid().nullable().optional(),
  accessType: FormAccessTypeEnum,
  password: z.string().nullable().optional(),
  allowedEmails: z.array(z.string()).default([]),
  settings: FormSettingsSchema.default({}),
  maxSubmissions: z.number().nullable().optional(),
  submissionCount: z.number().default(0),
  requireCaptcha: z.boolean().default(false),
  kioskMode: z.boolean().default(false),
  notifyOnSubmission: z.boolean().default(true),
  notificationEmails: z.array(z.string()).default([]),
  allowEmbed: z.boolean().default(true),
  embedDomains: z.array(z.string()).default([]),
  publishedAt: z.date().nullable().optional(),
  closedAt: z.date().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
  fields: z.array(FormFieldSchema).optional(),
  sections: z.array(FormSectionSchema).optional(),
})
export type Form = z.infer<typeof FormSchema>

// =====================
// Form Submission Types
// =====================

export const FormFieldValueSchema = z.object({
  id: z.string().uuid(),
  submissionId: z.string().uuid(),
  fieldId: z.string().uuid(),
  value: z.unknown(),
  createdAt: z.date().optional(),
})
export type FormFieldValue = z.infer<typeof FormFieldValueSchema>

export const SubmissionMetadataSchema = z.object({
  ip: z.string().optional(),
  userAgent: z.string().optional(),
  referrer: z.string().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
})
export type SubmissionMetadata = z.infer<typeof SubmissionMetadataSchema>

export const FormSubmissionSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  submitterEmail: z.string().nullable().optional(),
  submitterName: z.string().nullable().optional(),
  submitterId: z.string().uuid().nullable().optional(),
  status: FormSubmissionStatusEnum,
  processingError: z.string().nullable().optional(),
  createdModuleType: z.string().nullable().optional(),
  createdModuleId: z.string().uuid().nullable().optional(),
  metadata: SubmissionMetadataSchema.default({}),
  createdAt: z.date(),
  updatedAt: z.date().optional(),
  fieldValues: z.array(FormFieldValueSchema).optional(),
})
export type FormSubmission = z.infer<typeof FormSubmissionSchema>

// =====================
// Form Access Token Schema
// =====================

/**
 * Form access tokens are HASHED AT REST. The plaintext `token` is returned
 * exactly once — by the endpoint that mints it (POST /forms/:id/tokens,
 * validate-access, verify-code) — and is never readable again. Listings carry
 * only `tokenPrefix`, the non-secret handle used to tell tokens apart; the
 * recovery path for a lost link is revoke and re-invite.
 */
export const FormAccessTokenSchema = z.object({
  id: z.string().uuid(),
  formId: z.string().uuid(),
  /** One-time plaintext — present ONLY in a create/validate response. */
  token: z.string().optional(),
  /** First 8 characters, safe to display. Null on un-upgraded legacy rows. */
  tokenPrefix: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  usedAt: z.date().nullable().optional(),
  expiresAt: z.date(),
  createdAt: z.date().optional(),
})
export type FormAccessToken = z.infer<typeof FormAccessTokenSchema>

// =====================
// DTOs
// =====================

export const CreateFormDtoSchema = z.object({
  name: z.string().min(1).max(255),
  slug: z
    .string()
    .min(5, 'Slug must be at least 5 characters')
    .max(255)
    .regex(
      /^[a-z0-9-]+$/,
      'Slug can only contain lowercase letters, numbers, and hyphens',
    )
    .optional(),
  description: z.string().optional(),
  targetType: FormTargetTypeEnum,
  // Nullable so switching target type can explicitly clear the now-irrelevant
  // companion (e.g. MODULE→COLLECTION clears targetModuleType).
  targetModuleType: z.string().nullable().optional(),
  targetCollectionId: z.string().uuid().nullable().optional(),
  accessType: FormAccessTypeEnum.optional(),
  password: z.string().optional(),
  allowedEmails: z.array(z.string().email()).optional(),
  settings: FormSettingsSchema.optional(),
  maxSubmissions: z.number().positive().optional(),
  requireCaptcha: z.boolean().optional(),
  kioskMode: z.boolean().optional(),
  notifyOnSubmission: z.boolean().optional(),
  notificationEmails: z.array(z.string().email()).optional(),
  allowEmbed: z.boolean().optional(),
  embedDomains: z.array(z.string()).optional(),
  // When true (and targetType is COLLECTION with no targetCollectionId), the
  // server creates a new collection for this form and keeps its fields in
  // sync with the form's fields (one-to-one managed collection).
  createManagedCollection: z.boolean().optional(),
})
export type CreateFormDto = z.infer<typeof CreateFormDtoSchema>

export const UpdateFormDtoSchema = CreateFormDtoSchema.partial()
export type UpdateFormDto = z.infer<typeof UpdateFormDtoSchema>

export const CreateFormFieldDtoSchema = z.object({
  sectionId: z.string().uuid().nullable().optional(),
  fieldType: FormFieldTypeEnum,
  label: z.string().min(1).max(255),
  name: z.string().min(1).max(100),
  description: z.string().nullable().optional(),
  placeholder: z.string().max(255).nullable().optional(),
  helpText: z.string().nullable().optional(),
  footerText: z.string().nullable().optional(),
  mappedField: z.string().max(100).nullable().optional(),
  isRequired: z.boolean().optional(),
  validation: FieldValidationSchema.optional(),
  options: z.array(FieldOptionSchema).optional(),
  defaultValue: z.unknown().optional(),
  order: z.number().optional(),
  width: z.enum(['full', 'half', 'third']).optional(),
  conditions: z.array(ConditionalRuleSchema).nullable().optional(),
  fileSettings: FileSettingsSchema.optional(),
})
export type CreateFormFieldDto = z.infer<typeof CreateFormFieldDtoSchema>

export const UpdateFormFieldDtoSchema = CreateFormFieldDtoSchema.partial()
export type UpdateFormFieldDto = z.infer<typeof UpdateFormFieldDtoSchema>

export const CreateFormSectionDtoSchema = z.object({
  title: z.string().min(1).max(255),
  description: z.string().optional(),
  footerText: z.string().optional(),
  order: z.number().optional(),
  conditions: z.array(ConditionalRuleSchema).optional(),
})
export type CreateFormSectionDto = z.infer<typeof CreateFormSectionDtoSchema>

export const UpdateFormSectionDtoSchema = CreateFormSectionDtoSchema.partial()
export type UpdateFormSectionDto = z.infer<typeof UpdateFormSectionDtoSchema>

export const SubmitFormDtoSchema = z.object({
  values: z.record(z.string(), z.unknown()), // fieldId -> value
  submitterEmail: z.string().email().optional(),
  submitterName: z.string().optional(),
  captchaToken: z.string().optional(),
})
export type SubmitFormDto = z.infer<typeof SubmitFormDtoSchema>

export const ReorderFieldsDtoSchema = z.object({
  fieldIds: z.array(z.string().uuid()),
})
export type ReorderFieldsDto = z.infer<typeof ReorderFieldsDtoSchema>

export const ReorderSectionsDtoSchema = z.object({
  sectionIds: z.array(z.string().uuid()),
})
export type ReorderSectionsDto = z.infer<typeof ReorderSectionsDtoSchema>

export const ValidateAccessDtoSchema = z.object({
  token: z.string().optional(),
  password: z.string().optional(),
  email: z.string().email().optional(),
})
export type ValidateAccessDto = z.infer<typeof ValidateAccessDtoSchema>

export const GenerateAccessTokensDtoSchema = z.object({
  emails: z.array(z.string().email()),
  expiresInDays: z.number().positive().default(7),
  sendEmails: z.boolean().default(true),
})
export type GenerateAccessTokensDto = z.infer<
  typeof GenerateAccessTokensDtoSchema
>

// =====================
// Response Types
// =====================

export interface PublicFormResponse {
  id: string
  name: string
  slug: string
  description?: string | null
  status: FormStatus
  accessType: FormAccessType
  settings: FormSettings
  requireCaptcha: boolean
  kioskMode: boolean
  requiresAccessValidation: boolean
  allowEmbed: boolean
  embedDomains: string[]
  fields: FormField[]
  sections: FormSection[]
  organization?: {
    name: string
    logoUrl?: string | null
  }
  requiresPassword?: boolean
  requiresEmail?: boolean
  requiresAuth?: boolean
  forceShowWatermark?: boolean
}

export interface FormSubmissionResponse {
  id: string
  formId: string
  submitterEmail?: string | null
  submitterName?: string | null
  status: FormSubmissionStatus
  createdAt: Date
  fieldValues: FormFieldValue[]
}

export interface FormAnalytics {
  totalSubmissions: number
  submissionsToday: number
  submissionsThisWeek: number
  submissionsThisMonth: number
  conversionRate?: number
  averageCompletionTime?: number
  fieldCompletionRates: Record<string, number>
}

// =====================
// Field Type Display Info
// =====================

export const FORM_FIELD_TYPE_INFO: Record<
  FormFieldType,
  { label: string; description: string; category: string }
> = {
  SHORT_TEXT: {
    label: 'Short Text',
    description: 'Single line text input',
    category: 'Text',
  },
  LONG_TEXT: {
    label: 'Long Text',
    description: 'Multi-line text area',
    category: 'Text',
  },
  RICH_TEXT: {
    label: 'Rich Text',
    description: 'Formatted text with styling',
    category: 'Text',
  },
  EMAIL: {
    label: 'Email',
    description: 'Email address with validation',
    category: 'Text',
  },
  PHONE: {
    label: 'Phone',
    description: 'Phone number input',
    category: 'Text',
  },
  URL: { label: 'URL', description: 'Website URL', category: 'Text' },
  NUMBER: { label: 'Number', description: 'Numeric input', category: 'Number' },
  PERCENTAGE: {
    label: 'Percentage',
    description: '0-100 percentage',
    category: 'Number',
  },
  CURRENCY: {
    label: 'Currency',
    description: 'Monetary value',
    category: 'Number',
  },
  DATE: { label: 'Date', description: 'Date picker', category: 'Date & Time' },
  DATETIME: {
    label: 'Date & Time',
    description: 'Date and time picker',
    category: 'Date & Time',
  },
  TIME: { label: 'Time', description: 'Time picker', category: 'Date & Time' },
  DROPDOWN: {
    label: 'Dropdown',
    description: 'Single select from options',
    category: 'Selection',
  },
  MULTISELECT: {
    label: 'Multi-select',
    description: 'Multiple selections',
    category: 'Selection',
  },
  CHECKBOX: {
    label: 'Checkboxes',
    description: 'Multiple checkbox options',
    category: 'Selection',
  },
  RADIO: {
    label: 'Radio Buttons',
    description: 'Single selection radio',
    category: 'Selection',
  },
  BOOLEAN: {
    label: 'Yes/No',
    description: 'Toggle switch',
    category: 'Selection',
  },
  RATING: {
    label: 'Rating',
    description: 'Star rating scale',
    category: 'Selection',
  },
  FILE_UPLOAD: {
    label: 'File Upload',
    description: 'Single file upload',
    category: 'Files',
  },
  MULTI_FILE_UPLOAD: {
    label: 'Multiple Files',
    description: 'Multiple file uploads',
    category: 'Files',
  },
  REFERENCE: {
    label: 'Reference',
    description: 'Link to a record',
    category: 'Reference',
  },
  MULTI_REFERENCE: {
    label: 'Multi-Reference',
    description: 'Link to multiple records',
    category: 'Reference',
  },
  SECTION_HEADER: {
    label: 'Section Header',
    description: 'Visual section divider with title',
    category: 'Layout',
  },
  DESCRIPTION_BLOCK: {
    label: 'Description',
    description: 'Informational text block',
    category: 'Layout',
  },
  IMAGE: {
    label: 'Image',
    description: 'Creator-added image block',
    category: 'Layout',
  },
  DIVIDER: {
    label: 'Divider',
    description: 'Horizontal line separator',
    category: 'Layout',
  },
  PAGE_BREAK: {
    label: 'Page Break',
    description: 'Split form into pages',
    category: 'Layout',
  },
}

// Layout field types (don't capture data)
export const LAYOUT_FIELD_TYPES: FormFieldType[] = [
  'SECTION_HEADER',
  'DESCRIPTION_BLOCK',
  'IMAGE',
  'DIVIDER',
  'PAGE_BREAK',
]

export function isLayoutFieldType(type: FormFieldType): boolean {
  return LAYOUT_FIELD_TYPES.includes(type)
}

// =====================
// Conditional Logic Evaluation & Field Validation
// =====================
// Single source of truth used by both the public form renderer (apps/web) and
// the submission endpoint (apps/api). The server re-evaluates visibility and
// re-validates values so a crafted request can't bypass client-side rules.

/**
 * Loosely compare two values — handles string/number coercion
 * so "5" equals 5, and case-insensitive string matches.
 */
function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true
  // Both truthy strings — case-insensitive
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase()
  }
  // Numeric coercion: "5" == 5
  const numA = Number(a)
  const numB = Number(b)
  if (!isNaN(numA) && !isNaN(numB) && String(a) !== '' && String(b) !== '') {
    return numA === numB
  }
  // Boolean coercion: "true" == true
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return String(a) === String(b)
  }
  return false
}

/**
 * Compare two values as numbers. Falls back to date comparison when either
 * side is not a valid number. Returns null when neither comparison applies.
 */
function compareNumericOrDate(a: unknown, b: unknown): number | null {
  const numA = Number(a)
  const numB = Number(b)
  if (!isNaN(numA) && !isNaN(numB) && String(a) !== '' && String(b) !== '') {
    return numA - numB
  }
  const dateA = new Date(String(a))
  const dateB = new Date(String(b))
  if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
    return dateA.getTime() - dateB.getTime()
  }
  return null
}

/** Check if a value is considered "empty" for condition/required evaluation. */
export function isFormValueEmpty(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  )
}

/**
 * Check if a single condition is met based on the current form values.
 * Handles all field types: text, number, selection, boolean, date, file.
 */
export function evaluateFormCondition(
  condition: { fieldId: string; operator: ConditionOperator; value: unknown },
  values: Record<string, unknown>,
): boolean {
  const fieldValue = values[condition.fieldId]
  const targetValue = condition.value

  switch (condition.operator) {
    case 'equals':
      return looseEquals(fieldValue, targetValue)

    case 'not_equals':
      return !looseEquals(fieldValue, targetValue)

    case 'contains':
      // String: case-insensitive substring match
      if (typeof fieldValue === 'string' && typeof targetValue === 'string') {
        return fieldValue.toLowerCase().includes(targetValue.toLowerCase())
      }
      // Array (multiselect/checkbox): check if target is in the array
      if (Array.isArray(fieldValue)) {
        return fieldValue.some(v => looseEquals(v, targetValue))
      }
      return false

    case 'not_contains':
      if (typeof fieldValue === 'string' && typeof targetValue === 'string') {
        return !fieldValue.toLowerCase().includes(targetValue.toLowerCase())
      }
      if (Array.isArray(fieldValue)) {
        return !fieldValue.some(v => looseEquals(v, targetValue))
      }
      return true

    case 'greater_than': {
      const cmp = compareNumericOrDate(fieldValue, targetValue)
      return cmp !== null ? cmp > 0 : false
    }

    case 'less_than': {
      const cmp = compareNumericOrDate(fieldValue, targetValue)
      return cmp !== null ? cmp < 0 : false
    }

    case 'is_empty':
      return isFormValueEmpty(fieldValue)

    case 'is_not_empty':
      return !isFormValueEmpty(fieldValue)

    case 'is_checked':
      return fieldValue === true || fieldValue === 'true'

    case 'is_not_checked':
      return (
        fieldValue === false ||
        fieldValue === 'false' ||
        fieldValue === undefined ||
        fieldValue === null
      )

    default:
      // Unknown operator — treat as non-blocking (condition passes)
      return true
  }
}

/**
 * Evaluate an element's conditional rules against the current values.
 * Returns whether the element is visible and whether it is conditionally
 * required. Rules with no valid conditions are ignored.
 */
export function evaluateFormConditionalRules(
  rules: ConditionalRule[] | null | undefined,
  values: Record<string, unknown>,
): { isVisible: boolean; isRequired: boolean } {
  if (!rules || rules.length === 0) {
    return { isVisible: true, isRequired: false }
  }

  let isVisible = true
  let isRequired = false

  for (const rule of rules) {
    // Filter out conditions with missing fields (orphaned references)
    const validConditions = rule.conditions.filter(
      (
        c,
      ): c is {
        fieldId: string
        operator: ConditionOperator
        value: unknown
      } => !!c.fieldId && !!c.operator,
    )

    // A rule with no valid conditions should not affect visibility
    if (validConditions.length === 0) continue

    const conditionsMet =
      rule.logic === 'all'
        ? validConditions.every(c => evaluateFormCondition(c, values))
        : validConditions.some(c => evaluateFormCondition(c, values))

    if (rule.action === 'show' && !conditionsMet) {
      isVisible = false
    } else if (rule.action === 'hide' && conditionsMet) {
      isVisible = false
    } else if (rule.action === 'require' && conditionsMet) {
      isRequired = true
    }
  }

  return { isVisible, isRequired }
}

// Selection field types whose submitted values must come from the configured
// options list.
const OPTION_CONSTRAINED_FIELD_TYPES: FormFieldType[] = [
  'DROPDOWN',
  'RADIO',
  'MULTISELECT',
  'CHECKBOX',
]

export const DEFAULT_RATING_SCALE = 5

/**
 * The number of steps on a RATING field's scale. Configured via
 * `validation.max` (2–10); defaults to 5 stars.
 */
export function getRatingScale(field: Pick<FormField, 'validation'>): number {
  const max = (field.validation as FieldValidation | null | undefined)?.max
  return typeof max === 'number' && max >= 2 && max <= 10
    ? Math.floor(max)
    : DEFAULT_RATING_SCALE
}

/**
 * Validate a single field's value against its required flag, validation rules,
 * type-specific format checks, and configured options. Hidden fields (per
 * conditional rules) and layout fields are always valid. Returns a
 * human-readable error message, or null when the value is acceptable.
 */
export function validateFormFieldValue(
  field: Pick<
    FormField,
    | 'fieldType'
    | 'label'
    | 'isRequired'
    | 'validation'
    | 'options'
    | 'conditions'
  >,
  value: unknown,
  allValues: Record<string, unknown>,
): string | null {
  if (isLayoutFieldType(field.fieldType)) return null

  const { isVisible, isRequired: conditionallyRequired } =
    evaluateFormConditionalRules(
      field.conditions as ConditionalRule[] | null | undefined,
      allValues,
    )

  // Skip validation for hidden fields
  if (!isVisible) return null

  const isRequired = field.isRequired || conditionallyRequired

  if (isRequired && isFormValueEmpty(value)) {
    return `${field.label} is required`
  }

  // Skip further validation if no value
  if (value === undefined || value === null || value === '') {
    return null
  }

  const validation = field.validation as
    | Record<string, unknown>
    | null
    | undefined

  if (
    ['NUMBER', 'PERCENTAGE', 'CURRENCY'].includes(field.fieldType) &&
    (typeof value !== 'number' || Number.isNaN(value))
  ) {
    return `${field.label} must be a valid number`
  }

  if (field.fieldType === 'RATING') {
    const scale = getRatingScale(field)
    if (
      typeof value !== 'number' ||
      !Number.isInteger(value) ||
      value < 1 ||
      value > scale
    ) {
      return `${field.label} must be a rating between 1 and ${scale}`
    }
  }

  // Custom text validation only applies to free-text fields. Typed fields
  // like email, phone, URL, date, and time have dedicated validators below.
  if (
    validation &&
    typeof value === 'string' &&
    ['SHORT_TEXT', 'LONG_TEXT', 'RICH_TEXT'].includes(field.fieldType)
  ) {
    if (
      validation.minLength &&
      value.length < (validation.minLength as number)
    ) {
      return `${field.label} must be at least ${validation.minLength} characters`
    }
    if (
      validation.maxLength &&
      value.length > (validation.maxLength as number)
    ) {
      return `${field.label} must be at most ${validation.maxLength} characters`
    }
    if (validation.pattern) {
      try {
        const regex = new RegExp(validation.pattern as string)
        if (!regex.test(value)) {
          return (
            (validation.patternMessage as string) ||
            `${field.label} format is invalid`
          )
        }
      } catch {
        // Invalid pattern configured on the field — don't block submission
      }
    }
  }

  // Number range validation
  if (validation && typeof value === 'number') {
    if (validation.min != null && value < (validation.min as number)) {
      return `${field.label} must be at least ${validation.min}`
    }
    if (validation.max != null && value > (validation.max as number)) {
      return `${field.label} must be at most ${validation.max}`
    }
  }

  if (field.fieldType === 'EMAIL' && typeof value === 'string') {
    if (!isValidEmailInput(value)) {
      return 'Please enter a valid email address'
    }
  }

  if (field.fieldType === 'URL' && typeof value === 'string') {
    if (!isValidUrlInput(value)) {
      return 'Please enter a valid URL'
    }
  }

  if (field.fieldType === 'DATE' && !isValidDateInput(value)) {
    return 'Please enter a valid date'
  }

  if (field.fieldType === 'DATETIME' && !isValidDateTimeInput(value)) {
    return 'Please enter a valid date and time'
  }

  if (field.fieldType === 'TIME' && !isValidTimeInput(value)) {
    return 'Please enter a valid time'
  }

  if (field.fieldType === 'PHONE' && typeof value === 'string') {
    if (!isValidPhoneInput(value)) {
      return 'Please enter a valid phone number'
    }
  }

  // Selection fields: submitted values must come from the configured options.
  // Only enforced when options are configured, and only for string values —
  // the renderer submits option `value` strings (arrays for multi-select).
  if (
    OPTION_CONSTRAINED_FIELD_TYPES.includes(field.fieldType) &&
    Array.isArray(field.options) &&
    field.options.length > 0
  ) {
    const allowed = new Set(field.options.map(o => o.value))
    const selected = Array.isArray(value) ? value : [value]
    for (const v of selected) {
      if (typeof v === 'string' && !allowed.has(v)) {
        return `${field.label} contains an invalid option`
      }
    }
  }

  return null
}
